import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Theme, useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { makeStyles } from "tss-react/mui";
import ZonePlannerMapZones from "../ZonePlanner/ZonePlannerMapZones";
import CleaningZoneSelectionOverlay from "../ZonePlanner/CleaningZoneSelectionOverlay";
import ZoomTransformWrapper from "../ZonePlanner/ZoomTransformWrapper";
import Base64Image from "../../misc/Base64Image";
import CleaningZone from "../../../interfaces/robot/rooms/cleaning-zone";
import { useSharedSelector, useSharedActions, useSharedDispatch } from "../../../store/SharedProvider";
import {
  selectZonePlannerMouseState,
  selectZoneIdsSentToBackOrder,
} from "../../../store/room/zone-planner/zone-planner-selectors";
import { selectRoomById, selectRoomCleaningZones } from "../../../store/room/room-selectors";
import ZoneControllerMouseState from "../../../enums/robot/zone-planner/zone-controller-mouse-state";
import { EnableDrag, SendBelowIcon } from "../../../utils/icons/icons";
import ZoneOrderCard from "./ZoneSelect/ZoneOrderCard";
import CustomAlert from "../../Feedback/CustomAlert";

const useToolbarStyles = makeStyles()((theme) => ({
  mapControlsContainer: {
    position: "absolute",
    display: "flex",
    flexDirection: "column",
    right: theme.spacing(2),
    top: theme.spacing(2),
    zIndex: 2,
    backgroundColor: "white",
    maxHeight: "calc(100% - 16px)",
    overflow: "auto",
    marginBottom: theme.spacing(1),
    minWidth: theme.spacing(26),
    "&::-webkit-scrollbar": {
      width: theme.spacing(0.8),
    },
    "&::-webkit-scrollbar-track": {
      backgroundClip: "padding-box",
      backgroundColor: theme.palette.grey[100],
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundClip: "padding-box",
      backgroundColor: theme.palette.grey[300],
    },
  },
}));

/**
 * An inline floating toolbar that shows a fixed-order toggle and a draggable
 * zone list. It is purely data-driven (no Redux dependency) and is designed
 * to be placed on top of a map canvas inside a ZoomTransformWrapper.
 */
const ZoneSelectionInlineToolbar: React.FC<{
  fixedOrder: boolean;
  selectedZoneIds: string[];
  cleaningZonesIdName: { id: string; name: string | null }[];
  onToggleOrderZones: () => void;
  updateSelectedZoneOrder: (ids: string[], fixedOrder: boolean) => void;
  onHoverZoneChange: (id: string | null) => void;
}> = ({
  fixedOrder,
  selectedZoneIds,
  cleaningZonesIdName,
  onToggleOrderZones,
  updateSelectedZoneOrder,
  onHoverZoneChange,
}) => {
  const { classes } = useToolbarStyles();
  const { t } = useTranslation();
  const [draggingId, setDraggingId] = useState<string | null>(null);

  return (
    <Card className={classes.mapControlsContainer}>
      {/* Manual-order toggle */}
      <Stack px={1} pt={0.5} direction="row" alignItems="center">
        <FormControlLabel
          control={<Switch size="small" checked={fixedOrder} onChange={onToggleOrderZones} />}
          label={<Typography variant="caption">{t("use-fixed-order")}</Typography>}
          sx={{ m: 0 }}
        />
      </Stack>

      {/* Zone list – always visible; drag only active when fixedOrder is on */}
      {selectedZoneIds.length > 0 && (
        <React.Fragment>
          <Divider />
          <Stack pb={0.5}>
            {selectedZoneIds.map((zoneId) => {
              const zoneDetails = cleaningZonesIdName.find((cz) => cz.id === zoneId);
              return (
                <ZoneOrderCard
                  key={zoneId}
                  id={zoneId}
                  name={zoneDetails?.name ?? ""}
                  isDragging={draggingId === zoneId}
                  disableDragging={!fixedOrder}
                  onHover={onHoverZoneChange}
                  onDragStart={() => setDraggingId(zoneId)}
                  onDrop={() => setDraggingId(null)}
                  onDropTargetEnter={() => {
                    if (!draggingId || draggingId === zoneId) return;
                    const newOrder = [...selectedZoneIds];
                    const fromIndex = newOrder.indexOf(draggingId);
                    const toIndex = newOrder.indexOf(zoneId);
                    newOrder.splice(fromIndex, 1);
                    newOrder.splice(toIndex, 0, draggingId);
                    updateSelectedZoneOrder(newOrder, fixedOrder);
                  }}
                />
              );
            })}
          </Stack>
        </React.Fragment>
      )}
    </Card>
  );
};

/** Button placed in the bottom-right map controls (left of Recenter) to activate send-to-back mode. */
const SendToBackButton: React.FC = () => {
  const { t } = useTranslation();

  const theme = useTheme();

  const dispatch = useSharedDispatch();

  const { setZonePlannerMouseState } = useSharedActions();

  const zonePlannerMouseState = useSharedSelector((state) => selectZonePlannerMouseState(state));

  const isActive = zonePlannerMouseState === ZoneControllerMouseState.SendToBack;

  const handleClick = () => {
    dispatch(
      setZonePlannerMouseState(isActive ? ZoneControllerMouseState.Default : ZoneControllerMouseState.SendToBack),
    );
  };

  return (
    <Tooltip title={t("zoneplannercontrols-send-zone-to-back")}>
      <Button
        onClick={handleClick}
        style={{
          backgroundColor: isActive ? theme.palette.primary.main : undefined,
          color: isActive ? theme.palette.primary.contrastText : undefined,
        }}
      >
        <SendBelowIcon />
      </Button>
    </Tooltip>
  );
};

/**
 * Inline zone / landmark selection panel.
 *
 * Reads `roomData` and `cleaningZones` directly from the shared Redux store
 * given the `roomId` prop. The parent only needs to supply selection state and
 * optional room-switcher props.
 *
 * Map image rendering:
 * - When `mapSrc` is provided (e.g. a URL in the robot webapp) it is used
 *   directly as the `<img>` `src`.
 * - Otherwise `roomData.map` is rendered via `<Base64Image>` (cloud /
 *   common store environments where the map is stored as base64).
 */
const ZoneSelection: React.FC<{
  /** Room whose zones and map should be displayed. */
  roomId?: string | null;
  selectedZoneIds?: string[];
  selectedLandmarkId?: string;
  fixedOrder?: boolean;
  onZoneSelectionChanged: (selectedZoneIds: string[], fixedOrder: boolean) => void;
  onLandmarkSelectionChange?: (selectedLandmarkId: string) => void;
  /** Show a loading spinner instead of the map. */
  isLoading?: boolean;
  /** When provided and length > 1, shows a room selector dropdown. */
  roomsForSelection?: { id: string; name: string }[];
  /** Currently selected room (controlled by parent when roomsForSelection is used). */
  selectedRoomId?: string;
  onRoomChange?: (roomId: string) => void;
}> = ({
  roomId,
  selectedZoneIds,
  selectedLandmarkId,
  fixedOrder = false,
  onZoneSelectionChanged,
  onLandmarkSelectionChange,
  isLoading = false,
  roomsForSelection,
  selectedRoomId,
  onRoomChange,
}) => {
  const { t } = useTranslation();

  const matchesMdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const [zoomScale, setZoomScale] = useState(1);
  const [hoverZoneId, setHoverZoneId] = useState<string | null>(null);
  const [isDragEnabled, setIsDragEnabled] = useState(false);

  const zonePlannerMouseState = useSharedSelector((state) => selectZonePlannerMouseState(state));
  const zoneIdsSentToBackOrder = useSharedSelector((state) => selectZoneIdsSentToBackOrder(state));
  const isSendToBackMode = zonePlannerMouseState === ZoneControllerMouseState.SendToBack;

  const dispatch = useSharedDispatch();
  const { sendZoneToBack, setZonePlannerMouseState } = useSharedActions();

  // Fetch room data and cleaning zones from Redux.
  const effectiveRoomId = roomId ?? null;
  const roomData = useSharedSelector((state) => selectRoomById(state, effectiveRoomId ?? ""));
  const getZonesForRoom = useSharedSelector((state) => selectRoomCleaningZones(state));
  const cleaningZones: CleaningZone[] = effectiveRoomId != null ? getZonesForRoom(effectiveRoomId) : [];
  const cleaningZonesIdName = useMemo(() => cleaningZones.map((cz) => ({ id: cz.id, name: cz.name })), [cleaningZones]);

  // Sort zones so that back-ordered zones are rendered first (behind others).
  const sortedCleaningZones = useMemo(() => {
    const zones: CleaningZone[] = cleaningZones;
    if (zoneIdsSentToBackOrder.length === 0) return zones;
    const backSet = new Set(zoneIdsSentToBackOrder);
    return [
      ...zoneIdsSentToBackOrder
        .map((id) => zones.find((z: CleaningZone) => z.id === id))
        .filter((z): z is CleaningZone => z != null),
      ...zones.filter((z: CleaningZone) => !backSet.has(z.id)),
    ];
  }, [cleaningZones, zoneIdsSentToBackOrder]);

  const zoneClickHandler = (zoneId: string) => {
    if (isSendToBackMode) {
      dispatch(sendZoneToBack(zoneId));
      dispatch(setZonePlannerMouseState(ZoneControllerMouseState.Default));
      return;
    }

    if (selectedZoneIds != null) {
      const newSelectedZoneIds = selectedZoneIds.some((z) => z === zoneId)
        ? selectedZoneIds.filter((z) => z !== zoneId)
        : [...selectedZoneIds, zoneId];
      onZoneSelectionChanged(newSelectedZoneIds, fixedOrder);
    }
  };

  const landmarkClickHandler = (landmarkId: string) => {
    onLandmarkSelectionChange?.(landmarkId);
  };

  const hoverZoneChangeHandler = (id: string | null) => setHoverZoneId(id);

  const toggleFixedOrderHandler = () => onZoneSelectionChanged(selectedZoneIds ?? [], !fixedOrder);

  const updateSelectedZoneOrderHandler = (ids: string[], newFixedOrder: boolean) =>
    onZoneSelectionChanged(ids, newFixedOrder);

  const toggleDragHandler = () => {
    setIsDragEnabled((prevState) => !prevState);
  };

  const renderMapImage = (style?: React.CSSProperties) => {
    if (roomData?.map != null) {
      return <Base64Image imageString={roomData.map} alt={roomData.name} style={style} />;
    }
    return null;
  };

  const overlayImgStyle: React.CSSProperties = {
    display: "block",
    objectFit: "contain",
    objectPosition: "top",
  };

  return (
    <Stack sx={{ height: "100%" }} gap={1}>
      {/* Room selector (only shown when multiple rooms available) */}
      {roomsForSelection != null && roomsForSelection.length > 1 && (
        <FormControl size="small" fullWidth sx={{ flexShrink: 0 }}>
          <InputLabel id="zone-selection-room-label">{t("label-room")}</InputLabel>
          <Select
            labelId="zone-selection-room-label"
            value={selectedRoomId ?? ""}
            onChange={(e: SelectChangeEvent) => onRoomChange?.(e.target.value)}
            label={t("label-room")}
          >
            {roomsForSelection.map((room) => (
              <MenuItem key={room.id} value={room.id}>
                {room.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Map area */}
      <Box sx={{ flex: 1, minHeight: 0, position: "relative", backgroundColor: "#ebebeb", overflow: "hidden" }}>
        {isLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            <CircularProgress />
          </Box>
        ) : roomData != null ? (
          <React.Fragment>
            {/* Toolbar: zone ordering + send-to-back – only relevant in zone-selection mode */}
            {selectedZoneIds != null && (
              <ZoneSelectionInlineToolbar
                fixedOrder={fixedOrder}
                selectedZoneIds={selectedZoneIds}
                cleaningZonesIdName={cleaningZonesIdName ?? []}
                updateSelectedZoneOrder={updateSelectedZoneOrderHandler}
                onHoverZoneChange={hoverZoneChangeHandler}
                onToggleOrderZones={toggleFixedOrderHandler}
              />
            )}
            <ZoomTransformWrapper
              centerOnInit
              showRecenter
              disabled={matchesMdDown && !isDragEnabled}
              onZoomChange={setZoomScale}
              additionalControls={
                <React.Fragment>
                  {selectedZoneIds != null ? <SendToBackButton /> : null}
                  {matchesMdDown && (
                    <Tooltip title={t("zoneplannercontrols-toggle-drag")}>
                      <span>
                        <Button
                          onClick={toggleDragHandler}
                          sx={(theme) => ({
                            backgroundColor: isDragEnabled ? theme.palette.primary.main : "red",
                            color: isDragEnabled ? theme.palette.primary.contrastText : undefined,
                          })}
                        >
                          <EnableDrag color={isDragEnabled ? "primary" : "disabled"} />
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </React.Fragment>
              }
            >
              <div style={{ position: "relative", cursor: isSendToBackMode ? "cell" : undefined }}>
                {selectedZoneIds != null ? (
                  <React.Fragment>
                    {renderMapImage({ display: "block", maxWidth: "100%" })}
                    <CleaningZoneSelectionOverlay
                      roomData={roomData}
                      cleaningZones={sortedCleaningZones}
                      selectedZoneIds={selectedZoneIds}
                      onZoneToggle={zoneClickHandler}
                      zoomScale={zoomScale}
                      hoverZoneId={hoverZoneId}
                      onHoverZoneChange={hoverZoneChangeHandler}
                      isSendToBackMode={isSendToBackMode}
                    />
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <ZonePlannerMapZones
                      roomData={roomData}
                      zoomScale={zoomScale}
                      hoverZoneId={hoverZoneId}
                      scheduleLandmarkSelectionProps={
                        selectedLandmarkId == null
                          ? undefined
                          : {
                              selectedLandmarkId: selectedLandmarkId ?? "",
                              onLandmarkClickOverride: landmarkClickHandler,
                            }
                      }
                      isChristmasMode={false}
                      isWayPointMapViewable={false}
                      hidePaths
                    />
                    {renderMapImage(overlayImgStyle)}
                  </React.Fragment>
                )}
              </div>
            </ZoomTransformWrapper>
          </React.Fragment>
        ) : null}
        {selectedZoneIds != null && selectedZoneIds.length === 0 && (
          <CustomAlert
            severity="error"
            variant="standard"
            header={t("select-zones-to-continue")}
            sx={(theme) => ({
              zIndex: 5,
              position: "absolute",
              left: theme.spacing(1),
              top: theme.spacing(1),
              right: theme.spacing(1),
            })}
          />
        )}
        {selectedLandmarkId != null && selectedLandmarkId.length === 0 && (
          <CustomAlert
            severity="error"
            variant="standard"
            header={t("select-landmark-to-continue")}
            sx={(theme) => ({
              position: "absolute",
              left: theme.spacing(1),
              top: theme.spacing(1),
              right: theme.spacing(1),
            })}
          />
        )}
      </Box>
    </Stack>
  );
};

export default ZoneSelection;
